import clsx, { ClassValue } from 'clsx';
import { isNil } from 'lodash';
import React, { CSSProperties, Dispatch, ReactNode, SetStateAction, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { RCDropdown } from '@shared/components/RCDropdown';
import { PreventOutsideClick } from '@shared/scenes/workflows/views/components/preventOutsideClick';
import styles from './Dropdown.module.css';

interface ButtonFunctionProps {
  onClick: VoidFunction;
  isOpen: boolean;
}

type ButtonFunction = (props: ButtonFunctionProps) => ReactNode;

interface DropdownProps {
  button: ReactNode | ButtonFunction;
  style?: CSSProperties;
  closeOnSelect?: boolean;
  openByDefault?: boolean;
  allowOutsideClick?: boolean;
  visible?: boolean;
  setVisible?: Dispatch<SetStateAction<boolean>>;
  children: ReactNode;
  className?: ClassValue;
  enableScroll?: boolean;
  testId?: string;
}

export function Dropdown({
  button,
  style = {},
  closeOnSelect = false,
  openByDefault = false,
  allowOutsideClick = false,
  visible,
  setVisible,
  children,
  enableScroll = true,
  testId,
  className,
}: DropdownProps) {
  const [_showing, setShowing] = useState(openByDefault);
  const [showing] = useDebounce(_showing, 100);

  const visibleState = isNil(visible) ? showing : visible;
  const visibleSetter = isNil(setVisible) ? setShowing : setVisible;

  const triggerButton =
    typeof button === 'function'
      ? button({ onClick: () => visibleSetter(prev => !prev), isOpen: visibleState })
      : button;

  return (
    <RCDropdown
      trigger={['click']}
      visible={visibleState}
      onVisibleChange={visibleSetter}
      onOverlayClick={closeOnSelect ? () => setShowing(false) : undefined}
      overlay={() => (
        <div data-testid={testId}>
          <div
            style={style}
            className={clsx(styles.dropdown, { [styles.scroll]: enableScroll }, className)}
          >
            {children}
          </div>
          {!allowOutsideClick && (
            <PreventOutsideClick zIndex={0} onPress={() => visibleSetter(false)} />
          )}
        </div>
      )}
    >
      <>{triggerButton}</>
    </RCDropdown>
  );
}
